import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import debtService from '../services/debtService';

export const fetchDebts = createAsyncThunk(
  'debts/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await debtService.getAll();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addDebt = createAsyncThunk(
  'debts/add',
  async (debtData, { rejectWithValue }) => {
    try {
      const response = await debtService.create(debtData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateDebt = createAsyncThunk(
  'debts/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await debtService.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 4 sep 2026 (Augusto: "registré una transferencia... no se ve una
// reducción de deuda"): ninguna transacción tocaba nunca Debt.monto (ver
// comentario en backend_new/app/schemas/debt.py::DebtPago) -- este thunk
// llama al nuevo endpoint dedicado que sí lo resta.
export const payDebt = createAsyncThunk(
  'debts/pay',
  async ({ id, monto }, { rejectWithValue }) => {
    try {
      const response = await debtService.registrarPago(id, monto);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteDebt = createAsyncThunk(
  'debts/delete',
  async (id, { rejectWithValue }) => {
    try {
      await debtService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  summary: {
    totalDebt: 0,
    totalPaid: 0,
    remaining: 0,
    monthlyPayment: 0,
  },
};

const debtSlice = createSlice({
  name: 'debts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch debts
    builder
      .addCase(fetchDebts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDebts.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.summary = calculateSummary(action.payload);
      })
      .addCase(fetchDebts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Add debt
    builder
      .addCase(addDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDebt.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
        // FIX: esto recalculaba con [...state.list, action.payload], pero
        // state.list YA incluye action.payload por el push de la línea de
        // arriba — la deuda se contaba dos veces en el resumen (por eso el
        // total mostraba el doble de lo real).
        state.summary = calculateSummary(state.list);
      })
      .addCase(addDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update debt
    builder
      .addCase(updateDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDebt.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        state.summary = calculateSummary(state.list);
      })
      .addCase(updateDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Pay debt (registrar pago -- resta directo de Debt.monto)
    builder
      .addCase(payDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(payDebt.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        state.summary = calculateSummary(state.list);
      })
      .addCase(payDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete debt
    builder
      .addCase(deleteDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDebt.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(d => d.id !== action.payload);
        state.summary = calculateSummary(state.list);
      })
      .addCase(deleteDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// FIX: esto sumaba d.amount / d.paid / d.monthly_payment, campos en inglés
// que nunca existieron en la respuesta del backend (que usa nombre, tipo,
// monto, tasa_interes, pago_mensual — sin ningún concepto de "paid"). El
// resultado real de estas sumas siempre era NaN. Se corrige a los campos
// reales; como el backend no trackea pagos realizados, "totalPaid" y
// "remaining" quedan en 0 hasta que se agregue esa funcionalidad.
const calculateSummary = (debts) => {
  const totalDebt = debts.reduce((sum, d) => sum + (d.monto || 0), 0);
  const monthlyPayment = debts.reduce((sum, d) => sum + (d.pago_mensual || 0), 0);

  return {
    totalDebt,
    totalPaid: 0,
    remaining: totalDebt,
    monthlyPayment,
  };
};

export const { clearError } = debtSlice.actions;
export default debtSlice.reducer;
